import { useEffect, useMemo, useState } from 'react'

import { difference, keyBy, union, xor, capitalize, startCase, isEqual, sortBy } from 'lodash'
import { Columns3Cog } from 'lucide-react'
import { DefaultSheet, DefaultTooltip, Button, SheetBody, SheetFooter } from 'twyne-new.ui'

import ColumnsConfigPanel, { ColumnsSummary } from '@Components/ColumnsConfig/Panel'
import { moveOnPinToggle, reorderWithinSection, resolveColumnOrder } from '@Components/ColumnsConfig/utils'
import { columnKey, isColumnConfigurable, useGrid } from '@Components/Grid/context'

export default function TableColumns() {
  const { columnsConfigOpen: open, setColumnsConfigOpen: setOpen } = useGrid()
  const [items, setItems] = useState([])

  return <DefaultSheet
    title={<SheetTitle items={items} />}
    trigger={<TriggerButton />}
    triggerProps={{ asChild: true }}
    wrapperProps={{ open, onOpenChange: setOpen }}
    contentProps={{ side: 'right', size: 'sm' }}>
    <ColumnsConfig open={open} onClose={() => setOpen(false)} onItemsChange={setItems} />
  </DefaultSheet>
}

function SheetTitle({ items }) {
  return <div className='flex flex-col gap-1'>
    <span>Table columns</span>
    <ColumnsSummary items={items} />
  </div>
}

function TriggerButton() {
  return <DefaultTooltip content='Table columns' side='bottom' triggerProps={{ asChild: true }}>
    <Button variant='outline'>
      <Columns3Cog size='14' />
    </Button>
  </DefaultTooltip>
}

function ColumnsConfig({ open, onClose, onItemsChange }) {
  const {
    columns, hiddenColumns, setHiddenColumns, columnOrder, setColumnOrder,
    pinnedColumns, setPinnedColumns, defaultPinnedColumns, defaultHiddenColumns, resetColumnsConfig,
  } = useGrid()

  const definitions = useMemo(() => (columns || []).filter(isColumnConfigurable).map(column => ({
    key: columnKey(column),
    label: columnLabel(column),
  })), [columns])

  const allKeys = useMemo(() => definitions.map(({ key }) => key), [definitions])

  const [pinnedKeys, setPinnedKeys] = useState(pinnedColumns)
  const [checkedKeys, setCheckedKeys] = useState(() => difference(allKeys, hiddenColumns))
  const [orderKeys, setOrderKeys] = useState(() => resolveColumnOrder(columnOrder, allKeys))

  useEffect(() => {
    if (open) {
      setPinnedKeys(pinnedColumns)
      setCheckedKeys(difference(allKeys, hiddenColumns))
      setOrderKeys(resolveColumnOrder(columnOrder, allKeys))
    }
  }, [open, allKeys, hiddenColumns, columnOrder, pinnedColumns])

  const togglePinned = key => {
    const nextPinned = xor(pinnedKeys, [key])
    setPinnedKeys(nextPinned)
    setOrderKeys(keys => moveOnPinToggle(keys, key, nextPinned))
  }
  const toggleChecked = key => setCheckedKeys(keys => xor(keys, [key]))
  const setChecked = (keys, checked) => setCheckedKeys(current => checked ? union(current, keys) : difference(current, keys))
  const reorder = (sectionItems, nextItems) => setOrderKeys(keys => reorderWithinSection(keys, sectionItems, nextItems))

  const handleApply = () => {
    setHiddenColumns(difference(allKeys, checkedKeys))
    setPinnedColumns(pinnedKeys)
    setColumnOrder(orderKeys)
    onClose()
  }

  const handleReset = () => {
    resetColumnsConfig()
    onClose()
  }

  const isDirty = [
    !isEqual(sortBy(checkedKeys), sortBy(difference(allKeys, defaultHiddenColumns))),
    !isEqual(orderKeys, allKeys),
    !isEqual(sortBy(pinnedKeys), sortBy(defaultPinnedColumns)),
  ].some(Boolean)

  const items = useMemo(() => {
    const byKey = keyBy(definitions, 'key')
    return orderKeys.map(key => ({
      ...byKey[key],
      pinned: pinnedKeys.includes(key),
      checked: checkedKeys.includes(key),
    }))
  }, [definitions, orderKeys, pinnedKeys, checkedKeys])

  useEffect(() => {
    onItemsChange?.(items)
  }, [items, onItemsChange])

  return <>
    <SheetBody className='h-[calc(100vh-12%)] pt-4 px-0'>
      <ColumnsConfigPanel
        items={items}
        contentClassName='px-4'
        onReorder={reorder}
        onTogglePinned={togglePinned}
        onToggleChecked={toggleChecked}
        onSetChecked={setChecked}
      />
    </SheetBody>
    <SheetFooter className='absolute bottom-0 w-full flex items-center justify-between'>
      <Button variant='ghost' onClick={handleReset} disabled={!isDirty}>Reset to default</Button>
      <div className='flex items-center gap-2'>
        <Button variant='ghost' color='neutral' onClick={onClose}>Cancel</Button>
        <Button color='accent' onClick={handleApply} disabled={!checkedKeys.length}>Apply</Button>
      </div>
    </SheetFooter>
  </>
}

function columnLabel({ header, accessorKey, id }) {
  if (typeof header === 'string' && header.trim()) {
    return header
  }

  return capitalize(startCase(accessorKey ?? id))
}
